/**
 * Share-management runners (plan Task 7.16): the enum-only analytics of every surface
 * (`feature:storage_share_action` / `feature:storage_file_action` — never a share, path, snapshot or quota value), the
 * snapshot "Open" runner and the clean-up after a deleted share or snapshot (its tabs close, its pins go; the tree
 * refreshes through main's `azure-storage:mutated` event). The dialogs themselves open through `openShareDialog`.
 */
import type { FileShareProtocolState, MountCommandOs, StorageOperationError, StorageResourceRef } from '../../../../types/azureStorage';
import type { PinnedObjectRef } from '../../../../types/electron';
import { usePinnedObjectsStore } from '../../../database-objects/store/pinnedObjectsStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { isStorageExplorerTab } from '../../core/types/workspaceTypes';
import { trackStorageEvent } from '../services/storageAnalytics';
import { openStorageExplorerTab } from '../utils/openStorageExplorerTab';
import type { ShareActionSource } from './namedShareActions';

export type ShareAction = 'create' | 'delete' | 'quota' | 'snapshot_create' | 'snapshot_delete' | 'snapshot_open' | 'properties_open' | 'mount_command_copy';
export type ShareActionOutcome = 'ok' | 'refused' | 'error' | 'cancelled';
export type ShareActionMeta = { source: ShareActionSource; protocol: FileShareProtocolState };

const REFUSED = new Set(['authorization', 'authentication', 'not_found', 'invalid_input', 'conflict', 'unsupported']);

export function shareActionOutcome(error?: StorageOperationError): ShareActionOutcome {
  if (!error) return 'ok';
  if (error.category === 'cancelled') return 'cancelled';
  return REFUSED.has(error.category) ? 'refused' : 'error';
}

export function trackShareAction(action: ShareAction, outcome: ShareActionOutcome, meta: ShareActionMeta, extra?: { os: MountCommandOs }): void {
  trackStorageEvent('feature:storage_share_action', { action, outcome, source: meta.source, protocol: meta.protocol, ...(extra ?? {}) });
}

export function trackFileAction(action: 'properties_open', outcome: ShareActionOutcome, meta: ShareActionMeta): void {
  trackStorageEvent('feature:storage_file_action', { action, outcome, source: meta.source, protocol: meta.protocol });
}

/** Snapshot "Open": the files tab rooted at that snapshot (R1 renders its read-only banner). */
export function openShareSnapshotTab(args: { connectionId: string; share: string; snapshot: string } & ShareActionMeta): string | null {
  const tabId = openStorageExplorerTab({ connectionId: args.connectionId, root: { kind: 'share', share: args.share, snapshot: args.snapshot }, source: 'tree' });
  trackShareAction('snapshot_open', tabId ? 'ok' : 'error', args);
  return tabId;
}

/** A file-service ref on `share` (and, when given, exactly that snapshot). */
function onShare(resource: StorageResourceRef, share: string, snapshot?: string): boolean {
  if (resource.kind !== 'share' && resource.kind !== 'directory' && resource.kind !== 'file') return false;
  return resource.share === share && (snapshot === undefined || resource.snapshot === snapshot);
}

/** After a delete: every tab rooted on the share (live or snapshot — or only that snapshot) closes, its pins go. */
export async function closeShareSurfaces(connectionId: string, share: string, snapshot?: string): Promise<void> {
  const workspace = useWorkspaceStore.getState();
  for (const tab of [...workspace.tabs]) {
    if (isStorageExplorerTab(tab) && tab.content.connectionId === connectionId && onShare(tab.content.root, share, snapshot)) workspace.removeTab(tab.id);
  }
  const pins = usePinnedObjectsStore.getState();
  const known: readonly PinnedObjectRef[] = pins.byConnection[connectionId]?.pinnedObjects ?? [];
  const stale = known.filter((pin) => pin.kind === 'storage' && onShare(pin.resource, share, snapshot));
  for (const pin of stale) await pins.unpin(connectionId, pin);
}
